import type { WarriorInstance, PlayerId } from '@/types';
import type { BoardState } from '@/types/game.types';
import { BoardManager } from './BoardManager';

export interface DamageResult {
  targetId: string;
  damage: number;
  remainingHealth: number;
  isDefeated: boolean;
}

export interface HealResult {
  targetId: string;
  healAmount: number;
  newHealth: number;
}

export interface CombatResult {
  updatedWarriors: WarriorInstance[];
  damageResults: DamageResult[];
  defeatedWarriors: string[];
}

export interface HealingResult {
  updatedWarriors: WarriorInstance[];
  healResults: HealResult[];
}

export class CombatResolver {
  /**
   * Resolve an attack action
   */
  static resolveAttack(
    attacker: WarriorInstance,
    allWarriors: WarriorInstance[],
    _board: BoardState
  ): CombatResult {
    if (!attacker.position) {
      return {
        updatedWarriors: allWarriors,
        damageResults: [],
        defeatedWarriors: [],
      };
    }

    const isPlayer2 = attacker.ownerId === 'player2';
    const attackPositions = BoardManager.getAttackTargetPositions(
      attacker.position,
      attacker.definition.attackPattern,
      isPlayer2
    );

    const damageResults: DamageResult[] = [];
    const defeatedWarriors: string[] = [];
    const updatedWarriors = allWarriors.map(warrior => {
      if (!warrior.position) return warrior;

      // Check if this warrior is in the attack range and is an enemy
      const isTarget = attackPositions.some(
        pos => pos.row === warrior.position?.row && pos.col === warrior.position?.col
      );
      const isEnemy = warrior.ownerId !== attacker.ownerId;

      if (isTarget && isEnemy) {
        const damage = attacker.definition.stats.damage;
        const newHealth = Math.max(0, warrior.currentHealth - damage);
        const isDefeated = newHealth <= 0;

        damageResults.push({
          targetId: warrior.instanceId,
          damage,
          remainingHealth: newHealth,
          isDefeated,
        });

        if (isDefeated) {
          defeatedWarriors.push(warrior.instanceId);
        }

        return {
          ...warrior,
          currentHealth: newHealth,
          position: isDefeated ? null : warrior.position,
        };
      }

      return warrior;
    });

    // Update attacker's cooldown (reset after attack)
    const finalWarriors = updatedWarriors.map(w => {
      if (w.instanceId === attacker.instanceId) {
        return {
          ...w,
          attackCooldown: 0,
        };
      }
      return w;
    });

    return {
      updatedWarriors: finalWarriors,
      damageResults,
      defeatedWarriors,
    };
  }

  /**
   * Resolve a heal action
   */
  static resolveHeal(
    healer: WarriorInstance,
    allWarriors: WarriorInstance[]
  ): HealingResult {
    if (!healer.position || healer.definition.stats.healingPower <= 0) {
      return {
        updatedWarriors: allWarriors,
        healResults: [],
      };
    }

    const healPower = healer.definition.stats.healingPower;
    const healResults: HealResult[] = [];

    // Heal all warriors on the same team (not just adjacent)
    const updatedWarriors = allWarriors.map(warrior => {
      if (!warrior.position) return warrior;

      // Check if this warrior is on the same team
      const isTeammate = warrior.ownerId === healer.ownerId;

      if (isTeammate) {
        const maxHealth = warrior.definition.stats.health;
        const healAmount = Math.min(healPower, maxHealth - warrior.currentHealth);

        if (healAmount > 0) {
          const newHealth = warrior.currentHealth + healAmount;
          healResults.push({
            targetId: warrior.instanceId,
            healAmount,
            newHealth,
          });

          return {
            ...warrior,
            currentHealth: newHealth,
          };
        }
      }

      return warrior;
    });

    // Reset healer's cooldown after heal
    const finalWarriors = updatedWarriors.map(w => {
      if (w.instanceId === healer.instanceId) {
        return {
          ...w,
          healCooldown: 0,
        };
      }
      return w;
    });

    return {
      updatedWarriors: finalWarriors,
      healResults,
    };
  }

  /**
   * Check if a player has been defeated (all warriors eliminated)
   */
  static isPlayerDefeated(
    playerId: PlayerId,
    warriors: WarriorInstance[]
  ): boolean {
    const playerWarriors = warriors.filter(w => w.ownerId === playerId);
    return playerWarriors.every(w => w.position === null || w.currentHealth <= 0);
  }

  /**
   * Increment cooldowns for a warrior after taking an action
   */
  static incrementCooldowns(
    warrior: WarriorInstance,
    allWarriors: WarriorInstance[]
  ): WarriorInstance[] {
    return allWarriors.map(w => {
      if (w.instanceId === warrior.instanceId) {
        return {
          ...w,
          attackCooldown: w.attackCooldown + 1,
          healCooldown:
            w.definition.stats.healingPower > 0
              ? w.healCooldown + 1
              : 0,
          dashCooldown:
            w.definition.stats.dashFrequency > 0
              ? w.dashCooldown + 1
              : 0,
        };
      }
      return w;
    });
  }
}
